import React from "react";
import { Link } from "react-router-dom";
import "./Legal.css";

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <Link to="/" className="back-home">
        ← Back to Home
      </Link>
      <div className="legal-content">
        <h1 className="legal-title">Privacy Policy</h1>

        <p className="legal-subtitle">Last Updated: December 27, 2025</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            This privacy policy (the "Policy") describes M31-V1 LLC's ("M31-V1",
            "we", "our", "us") practices regarding personal data collected when
            you access m31v1.com (the "Site") or use the M31-V1 services,
            applications, or platform through the Site or by any other means
            (collectively, the "Services"). M31-V1 is an AI and technology
            company that develops intelligent applications to serve humanity and
            augment human potential.
          </p>
          <p>
            As used in this Policy, "personal data" means any information that
            relates to, describes, or could be used to identify an individual,
            directly or indirectly.
          </p>
          <p>
            <strong>Applicability:</strong> This Policy applies to personal data
            that M31-V1 collects, uses, and discloses, which may include: (i)
            data collected through the Services, (ii) data collected through the
            process of training M31-V1 machine learning algorithms, (iii) data
            collected through M31-V1 websites, and (iv) data collected from
            third-party sources. Third-party sources may include public
            databases, commercial data sources, and the public internet. When
            you make purchases, we use third-party payment processors to collect
            credit card or other financial information. M31-V1 does not store
            the credit card or payment information you provide, only
            confirmation that payment was made.
          </p>
          <p>
            This Policy does not apply to personal data about M31-V1 employees,
            candidates, and certain contractors and agents acting in similar
            roles.
          </p>
          <p>
            <strong>Changes:</strong> We may update this Policy periodically to
            reflect changes in legal, regulatory, or operational requirements,
            our practices, and other factors. Please check this Policy regularly
            for updates. If any changes are unacceptable to you, you should
            cease using the Services. When required under applicable law, we
            will notify you of any changes to this Policy.
          </p>
          <p>
            <strong>Definitions:</strong> Throughout this Policy, "You" or
            "Your" means the individual accessing or using the Services, or the
            company or legal entity on behalf of which such individual is
            accessing the Services. "Company," "we," "us," or "our" refers to
            M31-V1 LLC. "Usage Data" refers to data collected automatically
            through use of the Services or from the Services infrastructure
            itself (for example, the duration of a page visit).
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Collecting and Using Your Personal Data</h2>

          <h3>2.1 Types of Data Collected</h3>

          <h3>2.1.1 Personal Data</h3>
          <p>
            While interacting with the Services, you may provide certain
            personally identifiable information that could be used to contact or
            identify you. Personally identifiable information may include, but
            is not limited to:
          </p>
          <ul>
            <li>Your username for the Services</li>
            <li>
              Inputs that you provide (including text, images, and those
              generated from spoken input and summarized into text), and other
              content such as photos, videos, documents, and messages that you
              input into the Services
            </li>
            <li>Your IP address</li>
            <li>Usage Data</li>
            <li>Tracking Technologies and Cookies</li>
            <li>Contact Information</li>
            <li>
              Organizational Information such as your company title or
              affiliation
            </li>
            <li>Your Email address</li>
            <li>
              Other data that you elect to send to M31-V1, such as survey
              responses or information you include in communications to us
            </li>
          </ul>

          <h3>2.2 Use of Your Personal Data</h3>
          <p>M31-V1 may use Personal Data for the following purposes:</p>
          <ul>
            <li>
              <strong>To provide, maintain, and improve our Services:</strong>{" "}
              including to monitor the usage of our Services.
            </li>
            <li>
              <strong>To manage Your account:</strong> to manage your
              registration as a user of the Services. The Personal Data you
              provide can give you access to different functionalities available
              to registered users.
            </li>
            <li>
              <strong>For the performance of a contract:</strong> the
              development, compliance, and undertaking of the purchase contract
              for the products, items, or services you have purchased or any
              other contract with us through the Services.
            </li>
            <li>
              <strong>To contact You:</strong> to contact you by email,
              telephone, SMS, or other equivalent forms of electronic
              communication regarding updates or informative communications
              related to functionalities, products, or contracted services,
              including security updates when necessary or reasonable for their
              implementation.
            </li>
            <li>
              <strong>To provide You with news and offers:</strong> to provide
              you with news, special offers, and general information about other
              goods, services, and events which we offer that are similar to
              those you have already purchased or inquired about, unless you
              have opted not to receive such information.
            </li>
            <li>
              <strong>For marketing and advertising:</strong> including through
              cookies and similar technologies with your consent where
              applicable.
            </li>
            <li>
              <strong>To manage Your requests:</strong> to attend to and manage
              your requests to us.
            </li>
            <li>
              <strong>For business transfers:</strong> we may use your
              information to evaluate or conduct a merger, divestiture,
              restructuring, reorganization, dissolution, or other sale or
              transfer of some or all of our assets, whether as a going concern
              or as part of bankruptcy, liquidation, or similar proceeding, in
              which Personal Data held by us about our Services users is among
              the assets transferred.
            </li>
            <li>
              <strong>For other purposes:</strong> we may use your information
              for data analysis, identifying usage trends, determining the
              effectiveness of our promotional campaigns, and to evaluate and
              improve our Services, products, services, marketing, and your
              experience.
            </li>
          </ul>

          <h3>2.3 Sharing of Your Personal Data</h3>
          <p>
            We may share your personal information in the following situations:
          </p>
          <ul>
            <li>
              <strong>
                With Service Providers, Third-Party Vendors, Consultants, and
                Business Partners:
              </strong>{" "}
              we may share your personal information with these parties to
              provide the Services to you, monitor and analyze use of our
              Services, contact you, and for other purposes stated in our Terms
              of Service. This may include third-party analytics and advertising
              partners that use cookies or similar technologies, where permitted
              by law.
            </li>
            <li>
              <strong>For business transfers:</strong> we may share or transfer
              your personal information in connection with, or during
              negotiations of, any merger, sale of Company assets, financing, or
              acquisition of all or a portion of our business to another
              company.
            </li>
            <li>
              <strong>With Your consent:</strong> we may disclose your personal
              information for any other purpose with your consent.
            </li>
            <li>
              <strong>With Law Enforcement:</strong> Under certain
              circumstances, M31-V1 may be required to disclose your Personal
              Data if required by law or in response to valid requests by public
              authorities (e.g., a court or government agency). To the extent we
              receive a request from law enforcement for your personal data, we
              will promptly notify you and provide you with a copy of the
              request, unless we are legally prohibited from doing so.
            </li>
            <li>
              <strong>With Other Parties in order to:</strong>
              <ul>
                <li>Comply with a legal obligation</li>
                <li>
                  Protect and defend the rights or property of the Company
                </li>
                <li>
                  Prevent or investigate possible wrongdoing in connection with
                  the Services
                </li>
                <li>
                  Protect the personal safety of users of the Services or the
                  public
                </li>
                <li>Protect against legal liability</li>
              </ul>
            </li>
          </ul>

          <h3>2.4 Retention of Your Personal Data</h3>
          <p>
            The Company will retain your Personal Data only for as long as
            necessary for the purposes set out in this Privacy Policy. We will
            retain and use your Personal Data to the extent necessary to comply
            with our legal obligations (for example, if we are required to
            retain your data to comply with applicable laws), resolve disputes,
            and enforce our legal agreements and policies.
          </p>
          <p>
            The Company will also retain Usage Data for internal analysis
            purposes. Usage Data is generally retained for a shorter period,
            except when this data is used to strengthen security or improve the
            functionality of our Services, or we are legally obligated to retain
            this data for longer periods.
          </p>

          <h3>2.5 Transfer of Your Personal Data</h3>
          <p>
            Your information, including Personal Data, is processed at the
            Company's operating offices and in any other places where the
            parties involved in the processing are located. This means that this
            information may be transferred to — and maintained on — computers
            located outside of your state, province, country, or other
            governmental jurisdiction where the data protection laws may differ
            from those in your jurisdiction.
          </p>
          <p>
            Your consent to this Privacy Policy followed by your submission of
            such information represents your agreement to that transfer.
          </p>
          <p>
            The Company will take all steps reasonably necessary to ensure that
            your data is treated securely and in accordance with this Privacy
            Policy, and no transfer of your Personal Data will take place to an
            organization or country unless there are adequate controls in place
            including the security of your data and other personal information.
          </p>

          <h3>2.6 Security of Your Personal Data</h3>
          <p>
            The security of your Personal Data is important to us, but remember
            that no method of transmission over the Internet or method of
            electronic storage is 100% secure. While we strive to use
            commercially acceptable means to protect your Personal Data, we
            cannot guarantee its absolute security.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Children's Privacy</h2>
          <p>
            Our Services do not address anyone under the age of 13. We do not
            knowingly collect personally identifiable information from anyone
            under the age of 13. If you are a parent or guardian and you are
            aware that your child has provided us with Personal Data, please
            contact us. If we become aware that we have collected Personal Data
            from anyone under the age of 13 without verification of parental
            consent, we take steps to remove that information from our servers.
          </p>
          <p>
            If we need to rely on consent as a legal basis for processing your
            information and your country requires consent from a parent, we may
            require your parent's consent before we collect and use that
            information.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Links to Other Websites</h2>
          <p>
            Our Services may contain links to other websites that are not
            operated by us. If you click on a third-party link, you will be
            directed to that third party's site. We strongly advise you to
            review the Privacy Policy of every site you visit.
          </p>
          <p>
            Third-party sites and services have separate practices regarding the
            protection of personal data that you provide to them. We have no
            control over and assume no responsibility for the content, privacy
            policies, or practices of these third-party sites or services.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Changes to this Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page.
          </p>
          <p>
            We will let you know via email and/or a prominent notice on our
            Services, prior to the change becoming effective, and update the
            "Last Updated" date at the top of this Privacy Policy.
          </p>
          <p>
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Supplemental Terms for Certain Regions</h2>

          <h3>Europe</h3>
          <p>
            If you are located in the European Economic Area (the "EEA"),
            Switzerland, or the United Kingdom (the "UK"), our legal basis for
            collecting and using the Personal Data described in this Policy will
            depend on the personal data concerned and the specific context in
            which we collect it. However, we will normally collect personal data
            from you only where we have your consent to do so, where we need the
            personal data to perform a contract with you, or where the
            processing is in our legitimate interests and not overridden by your
            data protection interests or fundamental rights and freedoms. In
            some cases, we may also have a legal obligation to collect personal
            data from you.
          </p>
          <p>
            M31-V1 may share information internally or with third parties, as
            further described in this Policy. When we share Personal Data of
            individuals in the EEA, Switzerland, or UK with third parties, we
            make use of a variety of legal mechanisms to safeguard the transfer,
            including the European Commission-approved standard contractual
            clauses, as well as additional safeguards where appropriate.
          </p>
          <p>
            M31-V1 uses cookies and similar technologies for analytics and
            advertising purposes, including to help tailor content and ads.
            These non-essential cookies will only be used with your explicit
            consent. You can manage or withdraw your consent at any time by
            visiting the <Link to="/privacy-settings">Privacy Settings</Link>{" "}
            page. Essential cookies, which are necessary for the website and our
            Services to function, are always active.
          </p>
          <p>Additionally, you have the following data protection rights:</p>
          <ul>
            <li>
              You can request access, correction, updates, or deletion of your
              Personal Data.
            </li>
            <li>
              You can object to our processing of your Personal Data, ask us to
              restrict processing of your Personal Data, or request portability
              of your Personal Data.
            </li>
            <li>
              If we have collected and processed your Personal Data with your
              consent, then you can withdraw your consent at any time.
              Withdrawing your consent will not affect the lawfulness of any
              processing we conducted prior to your withdrawal, nor will it
              affect processing of your Personal Data conducted in reliance on
              lawful processing grounds other than consent.
            </li>
            <li>
              You have the right to complain to a data protection authority
              about our collection and use of your Personal Data.
            </li>
          </ul>
          <p>
            To exercise your rights, please contact us at{" "}
            <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a> and follow
            the instructions provided.
          </p>

          <h3>California</h3>
          <p>
            This section applies solely to residents of California or
            individuals whose information has been collected in California.
            M31-V1 has adopted this notice to comply with the California
            Consumer Privacy Act of 2018 ("CCPA"). Terms used in this section
            that are defined in the CCPA have the same meaning given therein.
          </p>

          <h3>Information We Collect</h3>
          <p>
            M31-V1 collects information that identifies, relates to, describes,
            or could reasonably be linked, directly or indirectly, with a
            particular California resident or household. The following describes
            categories of personal information we have collected and disclosed:
          </p>
          <ul>
            <li>
              <strong>Identifiers:</strong> Name, alias, postal address, unique
              personal identifier, online identifier, Internet Protocol (IP)
              address, email address, account name, or other similar
              identifiers.
              <em> (Collected: YES, Disclosed: YES)</em>
            </li>
            <li>
              <strong>
                Personal information under California Customer Records statute:
              </strong>{" "}
              Name, signature, address, telephone number, bank account number,
              credit card number, debit card number, or any other financial
              information. <em>(Collected: YES, Disclosed: YES)</em>
            </li>
            <li>
              <strong>Commercial information:</strong> Records of products or
              services purchased, obtained, or considered, or other purchasing
              or consuming histories or tendencies.{" "}
              <em>(Collected: YES, Disclosed: YES)</em>
            </li>
            <li>
              <strong>Internet or network activity:</strong> Browsing history,
              search history, information on interaction with a website,
              application, or advertisement.{" "}
              <em>(Collected: YES, Disclosed: YES)</em>
            </li>
            <li>
              <strong>Sensory data:</strong> Audio, electronic, visual
              information. (Only images you upload to our Services){" "}
              <em>(Collected: YES, Disclosed: YES)</em>
            </li>
            <li>
              <strong>Inferences:</strong> Profile reflecting preferences,
              characteristics, psychological trends, predispositions, behavior,
              attitudes, intelligence, abilities, and aptitudes.{" "}
              <em>(Collected: YES, Disclosed: YES)</em>
            </li>
          </ul>

          <h3>Your Rights and Choices</h3>
          <p>
            The CCPA provides California residents with specific rights
            regarding their Personal Information:
          </p>
          <p>
            <strong>
              Access to Specific Information and Data Portability:
            </strong>{" "}
            You have the right to request that M31-V1 disclose certain
            information about our collection and use of your Personal
            Information over the past twelve (12) months. Once we receive and
            confirm your verifiable request, we will disclose to you: (i) the
            categories of Personal Information we collected about you; (ii) the
            categories of sources for the Personal Information; (iii) our
            business or commercial purpose for collecting that Personal
            Information; (iv) the categories of third parties with whom we share
            that Personal Information; (v) the specific pieces of Personal
            Information we collected about you.
          </p>
          <p>
            <strong>Deletion Request Rights:</strong> You have the right to
            request that M31-V1 delete any of your Personal Information that we
            collected from you and retained. Once we receive and confirm your
            verifiable request, we will delete (and direct our service providers
            to delete) your personal information from our records, unless
            subject to certain limited exceptions.
          </p>
          <p>
            <strong>Do Not Sell or Share Opt-Out Rights:</strong> You have the
            right to opt-out of any "sale" or "sharing", as defined by the CCPA,
            of Personal Information by M31-V1. While M31-V1 does not sell your
            information, we may share it, for example, through advertising
            cookies that allow our third-party partners to deliver relevant and
            personalized ads. You can opt-out of such sharing at any time by
            updating your preferences on the{" "}
            <Link to="/privacy-settings">Privacy Settings</Link> page.
          </p>

          <h3>Exercising Your Rights</h3>
          <p>
            To exercise your access, data portability, deletion, or do-not-sell
            opt-out rights described above, please contact us at{" "}
            <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a>.
          </p>
          <p>
            You may only make a verifiable consumer request for access or data
            portability up to two times within a 12-month period. Any such
            request must: (i) provide sufficient information that allows us to
            reasonably verify that you are the person about whom we collected
            personal information or an authorized representative; and (ii)
            describe your request with sufficient detail such that we may
            understand, evaluate, and respond to it.
          </p>
          <p>
            Only you or a person registered with the California Secretary of
            State that you authorize to act on your behalf may make a verifiable
            consumer request related to your Personal Information. You may also
            make a verifiable consumer request on behalf of your minor child.
          </p>

          <h3>Response Timing and Format</h3>
          <p>
            We endeavor to respond to a verifiable consumer request within 45
            days of its receipt. If we require more time (up to 90 days), we
            will inform you in writing of the extension period and the reason
            for it. We will deliver required responses to you by email. Any
            disclosures we provide will only cover the 12-month period preceding
            the verifiable consumer request's receipt. For data portability
            requests, we will provide your personal information in a format that
            is readily usable and transferable.
          </p>
          <p>
            M31-V1 does not charge a fee to process or respond to your
            verifiable consumer request unless such requests become excessive,
            repetitive, or manifestly unfounded, or as otherwise permitted by
            the CCPA.
          </p>
        </section>

        <div className="legal-footer">
          <p>
            For questions about this Privacy Policy or to exercise your privacy
            rights, please contact us at{" "}
            <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
